/**
 * FocusRing — macOS 표준 포커스 인디케이터 (keyboard-only)
 *
 * macOS 시스템 포커스링은 `ring-2 ring-[#0A84FF]/60` 과 거의 동일.
 * `:focus-visible` 만 트리거되어 마우스 클릭 시엔 표시되지 않음 (Apple HIG).
 *
 * 사용법: 인터랙티브 요소를 감싸는 wrapper 로 사용. children 의 포커스 상태를
 *        listen 해서 ring 표시. 혹은 `asChild` 스타일이 필요하면 직접 부모에
 *        `admin-focus-ring` 유틸리티 클래스를 붙일 것.
 *
 * 접근성:
 *  - `:focus-visible` 만 사용 → 키보드 유저에게만 표시 (WCAG 2.4.7 Focus Visible)
 *  - `prefers-contrast: more` 환경에선 더 두꺼운 ring 자동 적용
 *  - ring offset 2px (색 대비 부족한 배경에서 확실히 보이도록)
 */

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../utils/cn';

interface FocusRingProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** 포커스 가능한 자식 요소를 wrapper 에 흡수시키려면 true (자식이 이미 focusable) */
  focusWithin?: boolean;
  /** Ring offset (기본 2px) */
  offset?: 0 | 1 | 2 | 4;
  /** Ring 색상 variant */
  variant?: 'blue' | 'danger' | 'success';
}

const OFFSET_CLASS: Record<NonNullable<FocusRingProps['offset']>, string> = {
  0: 'focus-visible:ring-offset-0 [&:has(:focus-visible)]:ring-offset-0',
  1: 'focus-visible:ring-offset-1 [&:has(:focus-visible)]:ring-offset-1',
  2: 'focus-visible:ring-offset-2 [&:has(:focus-visible)]:ring-offset-2',
  4: 'focus-visible:ring-offset-4 [&:has(:focus-visible)]:ring-offset-4',
};

const VARIANT_RING: Record<NonNullable<FocusRingProps['variant']>, string> = {
  blue: 'focus-visible:ring-[#0A84FF]/60 [&:has(:focus-visible)]:ring-[#0A84FF]/60',
  danger:
    'focus-visible:ring-[#FF453A]/60 [&:has(:focus-visible)]:ring-[#FF453A]/60',
  success:
    'focus-visible:ring-[#30D158]/60 [&:has(:focus-visible)]:ring-[#30D158]/60',
};

export function FocusRing({
  children,
  focusWithin = true,
  offset = 2,
  variant = 'blue',
  className,
  ...rest
}: FocusRingProps) {
  const focusClasses = focusWithin
    ? [
        'rounded-[inherit]',
        'transition-shadow duration-[100ms]',
        '[&:has(:focus-visible)]:outline-none',
        '[&:has(:focus-visible)]:ring-2',
        '[&:has(:focus-visible)]:ring-offset-white',
        'dark:[&:has(:focus-visible)]:ring-offset-[#1E1E1E]',
        VARIANT_RING[variant],
        OFFSET_CLASS[offset],
      ]
    : [
        'rounded-[inherit]',
        'transition-shadow duration-[100ms]',
        'focus-visible:outline-none',
        'focus-visible:ring-2',
        'focus-visible:ring-offset-white',
        'dark:focus-visible:ring-offset-[#1E1E1E]',
        VARIANT_RING[variant],
        OFFSET_CLASS[offset],
      ];

  return (
    <div className={cn(focusClasses, className)} {...rest}>
      {children}
    </div>
  );
}

FocusRing.displayName = 'FocusRing';

/**
 * 공용 focus-visible 유틸 클래스 — 단일 버튼/인풋에 직접 적용할 때.
 * Tailwind arbitrary 값을 쓰므로 `cn(focusRingClasses, ...)` 형태로 병합 가능.
 */
export const focusRingClasses = [
  'focus-visible:outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-[#0A84FF]/60',
  'focus-visible:ring-offset-2',
  'focus-visible:ring-offset-white',
  'dark:focus-visible:ring-offset-[#1E1E1E]',
  'transition-shadow duration-[100ms]',
].join(' ');
